































































































































































































import { Component, Prop, Watch, Mixins } from "vue-property-decorator";
import { mapGetters, mapState } from "vuex";
import { ValidationObserver } from "vee-validate";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import BrandsStore from "@/mixins/brandsStore";
import HasValidation from "@/mixins/HasValidation";

// Enums
import { ProductTypes } from "@/data/enums/products";
import { DataModules } from "@/store/modules/data";
import { ActionTypes } from "@/data/enums";
import { TranslationsTypes } from "@/data/enums/translations";

// Types
import { IState } from "@/store";
import { IProductCategory, IProductCategoryForm } from "@/models/products";

@Component({
  components: {
    "category-picker": () =>
      import("@/components/app/admin/catalogue/products/categoryPicker.vue"),
    "product-gallery": () =>
      import("@/components/app/admin/catalogue/products/productGallery.vue")
  },
  computed: {
    ...mapGetters({
      scopeGetter: "sortableCategories/getMainScope"
    }),
    ...mapState({
      rootCategoriesScope(state: IState, getters) {
        return getters[`data/${this.categoriesModule}/rootCategoriesScope`](
          this.brandId,
          this.productTypes
        );
      },
      rootCategories(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: this.categoriesModule,
          scope: this.rootCategoriesScope
        });
      },
      categoriesScope(state: IState, getters) {
        return getters[`data/${this.categoriesModule}/categoriesScope`](
          this.brandId,
          this.productTypes
        );
      },
      categoryScope(state: IState, getters) {
        return getters[`data/${this.categoriesModule}/scope`](this.categoryId);
      },
      category(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: this.categoriesModule,
          scope: this.categoryScope
        });
      }
    })
  }
})
export default class AddEditProductCategoryModal extends Mixins(
  BrandsStore,
  RequestPromises,
  HasValidation
) {
  public declare $refs: {
    name: HTMLFormElement;
    observer: InstanceType<typeof ValidationObserver>;
  };

  // Getters
  public scopeGetter!: string;
  public rootCategoriesScope!: string;
  public rootCategories!: IProductCategory[];
  public categoriesScope!: string;
  public categoryScope!: string;
  public category!: IProductCategory;

  // Props
  @Prop({ required: true })
  public productTypes!: ProductTypes[];

  @Prop({ default: ActionTypes.CREATE })
  public actionType!: string;

  @Prop({ default: null })
  public parentCategoryId!: IProductCategory["id"];

  @Prop({ default: null })
  public categoryId!: IProductCategory["id"];

  @Prop({ default: 1 })
  public numberOfModals!: number;

  public form = {
    name: null,
    description: "",
    short_description: "",
    brand_id: "",
    multiple: false,
    required: false,
    parent_id: null,
    price_override: false
  } as IProductCategoryForm;
  public formClone = {} as IProductCategoryForm;

  public categoriesModule: string = DataModules.CATALOGUE_CATEGORIES;
  public sortableCategoriesModule = "sortableCategories";

  public isLoading = false;
  public isReloading = false;
  public requestError = false;

  public isSubcategory = false;
  public isProcessing = false;

  public created() {
    if (this.categoryId && this.isUpdate) {
      this.isLoading = true;
    }
    this.loadData();
  }

  public async loadData() {
    try {
      await this.getRootCategories(this.isReloading);

      if (this.isUpdate) {
        await this.getCategory();
      }

      this.fillForm();
      this.requestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    }

    this.isReloading = false;
    this.isLoading = false;

    this.$nextTick(() => {
      if (this.$_.has(this.$refs, "name")) {
        this.$refs.name.focus();
      }
    });
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public get isUpdate() {
    return this.actionType === ActionTypes.UPDATE;
  }

  public getCategory() {
    return this.$store.dispatch(`data/${this.categoriesModule}/get`, {
      productTypes: this.productTypes,
      id: this.categoryId,
      brandId: this.brandId,
      scope: this.categoryScope,
      vm: this
    });
  }

  public getRootCategories(forceReload = false) {
    return this.$store.dispatch(`data/${this.categoriesModule}/list`, {
      productTypes: this.productTypes,
      scope: this.rootCategoriesScope,
      params: {
        order: "order",
        limit: 0,
        "filter[level]": 1
      },
      vm: this,
      ignoreStored: forceReload
    });
  }

  public fillForm() {
    this.form.brand_id = this.brandId;

    if (this.isUpdate) {
      this.form = this.$_.merge(
        this.form,
        this.$_.pick(this.category, [
          "name",
          "description",
          "short_description",
          "multiple",
          "required",
          "parent_id",
          "price_override"
        ])
      );
    }

    if (this.parentCategoryId || this.$_.get(this.category, "parent_id")) {
      this.isSubcategory = true;
    }

    this.formClone = this.$_.cloneDeep(this.form);
  }

  public get getTitle() {
    return this.$t(
      `_action.${
        this.actionType === ActionTypes.CREATE ? "add" : "edit"
      }_object`,
      {
        object: (this.$t(
          `_.product_${this.getTypeName()}category`
        ) as string).toLowerCase()
      }
    );
  }

  public get formIsValid() {
    if (this.$_.isEmpty(this.form.name)) {
      return false;
    }
    if (this.$_.isEqual(this.form, this.formClone)) {
      return false;
    }

    return true;
  }

  public get formIsDisabled() {
    return this.isProcessing || !this.formIsValid || this.hasErrors;
  }

  public validateBeforeSubmit() {
    this.isProcessing = true;
    this.validate()
      .then(result => {
        if (result) {
          return this.actionType === ActionTypes.CREATE
            ? this.createProductCategory()
            : this.updateProductCategory();
        }
        throw this.$t("_sentence.form_is_not_valid");
      })
      .then(async (result: IProductCategory) => {
        await this.getRootCategories(true);

        if (this.actionType === ActionTypes.CREATE && this.scopeGetter) {
          const data = this.$_.merge(
            this.$_.pick(result, ["id", "name", "order"]),
            {
              itemsTotalMsg: this.itemsCounter()
            }
          );

          if (!this.$_.get(result, "parent_id")) {
            await this.$store.commit(
              `${this.sortableCategoriesModule}/addParentCategory`,
              {
                scope: this.scopeGetter,
                data
              }
            );
          } else {
            await this.$store.commit(
              `${this.sortableCategoriesModule}/addNewCategory`,
              {
                scope: this.scopeGetter,
                categoryId: this.$_.get(result, "parent_id"),
                data
              }
            );
          }
        }

        this.$buefy.toast.open({
          message: this.$t(
            `_.object_successfully_${
              this.actionType === ActionTypes.CREATE ? "added" : "updated"
            }`,
            {
              object: this.$t(`_.product_${this.getTypeName()}category`)
            }
          ) as string
        });

        this.$emit("success", result);
        this.$emit("close");
      })
      .catch(error => {
        this.$store.dispatch("api/handleValidationError", {
          error,
          vm: this
        });
      })
      .finally(() => {
        this.isProcessing = false;
      });
  }

  public createProductCategory() {
    return this.$store.dispatch(
      `data/${DataModules.CATALOGUE_CATEGORIES}/create`,
      {
        data: this.form,
        productTypes: this.productTypes,
        scope: this.categoriesScope
      }
    );
  }

  public updateProductCategory() {
    return this.$store.dispatch(
      `data/${DataModules.CATALOGUE_CATEGORIES}/update`,
      {
        id: this.categoryId,
        productTypes: this.productTypes,
        data: this.$_.omitBy(this.form, (v, k) => {
          return this.formClone[k] === v;
        }),
        scope: this.categoriesScope
      }
    );
  }

  public getTypeName(beginning = "") {
    return this.$_.find(
      this.productTypes,
      type => type === ProductTypes.PRODUCT_OPTION
    )
      ? `${beginning}options_`
      : this.$_.find(
          this.productTypes,
          type => type === ProductTypes.PRODUCT_ATTRIBUTE
        )
      ? `${beginning}attributes_`
      : `${beginning}`;
  }

  public openTranslateModal(props: Record<string, string>) {
    if (!this.canTranslateProduct) return;
    this.$store.dispatch(
      `data/${DataModules.TRANSLATIONS}/openTranslationsModal`,
      {
        props: {
          objectId: this.category.id,
          objectTypeCode: TranslationsTypes.PRODUCTS_CATEGORY,
          brandId: this.category.brand_id,
          fieldType: "text",
          ...props
        }
      }
    );
  }

  public get isOption() {
    return this.productTypes.includes(ProductTypes.PRODUCT_OPTION);
  }
  public get isAttribute() {
    return this.productTypes.includes(ProductTypes.PRODUCT_ATTRIBUTE);
  }

  public itemsCounter() {
    return this.isOption
      ? "_plurals.product_options"
      : this.isAttribute
      ? "_plurals.product_attributes"
      : "_plurals.products";
  }

  public get guardFunctionalities() {
    if (this.actionType === ActionTypes.CREATE) {
      return this.isOption
        ? ["create_product_options_category"]
        : this.isAttribute
        ? ["create_product_attributes_category"]
        : ["create_product_category"];
    } else {
      return this.isOption
        ? ["update_product_options_category"]
        : this.isAttribute
        ? ["update_product_attributes_category"]
        : ["update_product_category"];
    }
  }

  public get canTranslateProduct() {
    return (
      this.$userCan("create_object_translation") &&
      this.$userCan("get_object_translation") &&
      this.$userCan("list_object_translations")
    );
  }

  @Watch("isSubcategory")
  public onIsSubcategoryChange() {
    if (this.isSubcategory) {
      this.form.parent_id = this.$_.get(
        this.category,
        "parent_id",
        this.parentCategoryId
      );
    } else {
      this.form.parent_id = null;
    }
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binList", {
      storeModule: DataModules.CATALOGUE_CATEGORIES,
      scope: this.rootCategoriesScope,
      vm: this
    });

    this.$store.dispatch("data/bin", {
      storeModule: this.categoriesModule,
      scope: this.categoryScope,
      vm: this
    });
  }
}
